/* =============================================================
   Timantti — Sticky Add to Cart
   Honey Whale / timantti-sticky-atc.js
   - Slides up once the main add to cart button leaves the top of the viewport
   - Its button submits the main product form, so product-form.js does the add
     and opens the cart drawer with the chosen variant + quantity
   - Mirrors price / variant / availability from Dawn's variant-change event
   Vanilla only. No build step required.
   ============================================================= */

if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.formId = this.dataset.formId;
        this.button = this.querySelector('[data-sticky-button]');
        this.label = this.querySelector('[data-sticky-label]');
        this.scrolledPast = false;
        this.forcedOpen = false;

        this.onButtonClick = this.onButtonClick.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onBlockDeselect = this.onBlockDeselect.bind(this);

        this.button?.addEventListener('click', this.onButtonClick);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers = [
            subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this)),
            subscribe(PUB_SUB_EVENTS.cartError, this.onCartError.bind(this)),
          ];
        }

        this.observeMainButton();
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect();
        this.stopLoading();
        this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
        this.button?.removeEventListener('click', this.onButtonClick);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.body.classList.remove('tt-sticky-atc-open');
      }

      get mainButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      observeMainButton() {
        const mainButton = this.mainButton;
        if (!mainButton || !('IntersectionObserver' in window)) return;

        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          // Only once the button has left through the top. On short screens it can
          // start below the fold, and the bar shouldn't appear before shoppers reach it.
          this.scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.update();
        });
        this.visibilityObserver.observe(mainButton);
      }

      update() {
        const visible = this.scrolledPast || this.forcedOpen;
        this.classList.toggle('is-visible', visible);
        // Body padding reserves room at the page bottom so the bar never covers the footer
        document.body.style.setProperty('--tt-sticky-atc-height', `${this.offsetHeight}px`);
        document.body.classList.toggle('tt-sticky-atc-open', visible);
      }

      onButtonClick(event) {
        const mainButton = this.mainButton;
        // product-form.js ignores submits while an add is in flight, so a spinner
        // started now would never be cleared
        if (!mainButton || mainButton.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }
        this.startLoading(mainButton);
      }

      startLoading(mainButton) {
        this.button.classList.add('loading');
        this.button.setAttribute('aria-disabled', 'true');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        // product-form.js owns the loading state on the main button; following it
        // clears the bar on success and error alike
        this.loadingObserver?.disconnect();
        this.loadingObserver = new MutationObserver(() => {
          if (!mainButton.classList.contains('loading')) this.stopLoading();
        });
        this.loadingObserver.observe(mainButton, { attributes: true, attributeFilter: ['class'] });
        this.loadingTimeout = setTimeout(() => this.stopLoading(), 10000);
      }

      stopLoading() {
        this.loadingObserver?.disconnect();
        clearTimeout(this.loadingTimeout);
        if (!this.button) return;
        this.button.classList.remove('loading');
        this.button.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      onVariantChange({ data }) {
        if (!data || data.sectionId !== this.sectionId || !data.html) return;

        // The payload is this section re-rendered for the new variant, so the fresh
        // bar and main button already carry the right price, title and availability
        const freshBar = data.html.querySelector('sticky-atc');
        ['[data-sticky-price]', '[data-sticky-variant]'].forEach((selector) => {
          const current = this.querySelector(selector);
          const fresh = freshBar?.querySelector(selector);
          if (current && fresh) current.innerHTML = fresh.innerHTML;
        });

        const freshMainButton = data.html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (freshMainButton && this.button) {
          this.button.disabled = freshMainButton.hasAttribute('disabled');
          const freshLabel = freshMainButton.querySelector('span');
          if (freshLabel && this.label) this.label.textContent = freshLabel.textContent.trim();
        }
      }

      onCartError(event) {
        if (!event || event.source !== 'product-form' || !this.classList.contains('is-visible')) return;

        // The error message sits beside the main button, out of view. Wait a frame:
        // product-form.js unhides it right after publishing this event.
        requestAnimationFrame(() => {
          const errorWrapper = document
            .getElementById(this.formId)
            ?.closest('product-form')
            ?.querySelector('.product-form__error-message-wrapper');
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          errorWrapper?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        });
      }

      // Theme editor: show the bar while its block is selected so it can be styled
      onBlockSelect(event) {
        if (event.target !== this) return;
        this.forcedOpen = true;
        this.update();
      }

      onBlockDeselect(event) {
        if (event.target !== this) return;
        this.forcedOpen = false;
        this.update();
      }
    }
  );
}
